import { useParams } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import { InputMultiSelect } from '@/design_system/InputMultiSelect/InputMultiSelect';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { BrandWithRelations, useBrand, useBrands, useUpdateBrand } from '@/models/brand';
import { useSettingsScope } from '@/routes/SettingsBeta/SettingsContext';

const ALL_BRANDS_OPTION_ID = '__all_brands__';

type BrandOption = {
  id: string;
  name: string;
};

export const RelatedBrandsTab = () => {
  const { id } = useParams();
  const { data: brand } = useBrand(id);

  if (!brand) {
    return null;
  }

  return <BrandsThatCanReplaceBrand brand={brand} />;
};

const BrandsThatCanReplaceBrand = ({ brand }: { brand: BrandWithRelations }) => {
  const { t } = useLingui();
  const { scope } = useSettingsScope();
  const organizationId = scope?.scope === 'organization' ? scope.organizationId : undefined;
  const { data } = useBrands({ organizationId, limit: 500, offset: 0 });
  const { mutateAsync: updateBrand } = useUpdateBrand(brand.id);

  const brandName = brand.name;
  const brandOptions: BrandOption[] = data?.brands ?? [];
  const brandIdsThatCanReplaceBrand = brand.brandsThatCanReplaceBrand
    ? brand.brandsThatCanReplaceBrand.map(({ id }) => id)
    : [];
  // Empty relation means every brand is allowed (see get-products brandIdToReplace filter)
  const isAllBrandsSelected = brandIdsThatCanReplaceBrand.length === 0;

  const allBrandsOption: BrandOption = {
    id: ALL_BRANDS_OPTION_ID,
    name: t({
      id: 'settings.organization.data-catalogs.brands.related-brands.all-brands',
      message: 'All brands',
    }),
  };

  const label = t({
    id: 'settings.organization.data-catalogs.brands.tab.related-brands.brands-that-can-replace-brand',
    message: `Brands allowed to replace ${brandName}`,
  });

  const selectedValue: BrandOption[] = isAllBrandsSelected
    ? [allBrandsOption]
    : brandOptions.filter((option) => brandIdsThatCanReplaceBrand.includes(option.id));

  const persistBrandIds = (brandIds: string[]) =>
    updateBrand({
      brandIdsThatCanReplaceBrand: brandIds,
    });

  return (
    <Box>
      <Stack gap="0.5em">
        <Title as="h3" size="300" weight="medium">
          {label}
        </Title>

        <Paragraph>
          <Trans id="settings.organization.data-catalogs.brands.related-brands.brands-that-can-replace-brand.description">
            When a product needs to be exchanged, only products with the following brands will be
            available
          </Trans>
        </Paragraph>

        <InputMultiSelect
          variant="select"
          aria-label={label}
          placeholder={label}
          isSearchable
          value={selectedValue}
          options={[allBrandsOption, ...brandOptions]}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name}
          onChange={(options) => {
            const hasAllOption = options.some((option) => option.id === ALL_BRANDS_OPTION_ID);
            const specificOptions = options.filter((option) => option.id !== ALL_BRANDS_OPTION_ID);

            if (hasAllOption && !isAllBrandsSelected) {
              persistBrandIds([]);

              return;
            }

            persistBrandIds(specificOptions.map(({ id }) => id));
          }}
          onRemoveValue={(brandIdToRemove) => {
            if (brandIdToRemove === ALL_BRANDS_OPTION_ID) {
              return;
            }

            persistBrandIds(
              brandIdsThatCanReplaceBrand.filter((_brandId) => _brandId !== brandIdToRemove)
            );
          }}
        />
      </Stack>
    </Box>
  );
};
